import * as React from 'react';
import { Currency } from './enums';
import { Location } from './LocationAutocomplete/types';
import { EngineTheme } from './theme';
import {
	BaggageType,
	GdsServiceRestrictionType,
	GdsServicesFragment,
	GdsServiceVipSubType,
	OrderAdditionalServiceGdsServiceServiceType,
	PassengerFragment,
	TravellerFieldEnum
} from '@websky/graphql';

export type Omit<T, K extends keyof T> = Pick<T, Exclude<keyof T, K>>;

export type UseSelectable<Enum> = (
	initialState: Enum
) => {
	active: Enum;
	onSelect: (element: Enum) => Enum;
};

export type UseToggleable = (
	initialState: boolean
) => {
	isOpen: boolean;
	open: () => void;
	close: () => void;
	toggle: () => void;
	setOpen: React.Dispatch<boolean>;
};

export interface WithThemeProp {
	theme: EngineTheme;
}

export interface WithClassName {
	className?: string;
}

export interface Money {
	amount: number;
	currency: Currency | string;
}

export interface Duration {
	days?: number;
	hours?: number;
	minutes?: number;
	seconds?: number;
}

export enum LocationType {
	Departure = 'departure',
	Arrival = 'arrival'
}

export interface DatesConfig {
	departure?: Date;
	return?: Date;
}

export interface LocationsConfig {
	departure?: Location;
	arrival?: Location;
}

export enum Mode {
	Booking,
	Checkin
}

export enum Locale {
	Georgian = 'ka',
	English = 'en',
	Russian = 'ru',
	German = 'de',
	Kazakh = 'kk',
	French = 'fr',
	Chinese = 'zh',
	Armenian = 'hy',
	Turkish = 'tr',
	Tajik = 'tg',
	Czech = 'cs',
	Belarusian = 'be',
	Hungarian = 'hu',
	Uzbek = 'uz',
	Serbian = 'sr',
	Korean = 'ko'
}

export interface ShippingRule {
	name: string;
	url: string;
}

export interface AdditionalService {
	url?: string;
	enabled: boolean;
}

export enum Metasearch {
	Aviasales = 'aviasales'
}

export enum ApolloErrorType {
	PnrBlocked = 'pnrBlocked'
}

export interface ApolloErrorPayload {
	operationName: string;
	message: string;
	type?: ApolloErrorType;
}

export type ApolloErrorEvent = Event & {
	detail?: ApolloErrorPayload;
};

export interface DateFormatOptions {
	weekStartsOn?: 0 | 1 | 2 | 3 | 4 | 5 | 6;
	firstWeekContainsDate?: number;
	useAdditionalWeekYearTokens?: boolean;
	useAdditionalDayOfYearTokens?: boolean;
}

export type DeepPartial<T> = {
	[P in keyof T]?: T[P] extends (infer U)[]
		? DeepPartial<U>[]
		: T[P] extends ReadonlyArray<infer U>
		? ReadonlyArray<DeepPartial<U>>
		: DeepPartial<T[P]>;
};

export type MUIChangeEvent =
	| React.ChangeEvent<Omit<HTMLInputElement, 'value'> & { value: string }>
	| (Event & { target: { value: string; name: string } });

export enum StepsEnum {
	Flights = 'Flights',
	Seats = 'Seats',
	Baggage = 'Baggage',
	Restrictions = 'Restrictions',
	Meal = 'Meal',
	Passengers = 'Passengers',
	Insurance = 'Insurance',
	Order = 'Order',
	Extras = 'Extras',
	Payment = 'Payment',
	BoardingPass = 'BoardingPass',
	Upgrade = 'Upgrade'
}

export interface CommonTravellerType {
	id?: string;
	type?: string;
	supplierTravellerType?: string;
	values?: {
		type?: TravellerFieldEnum;
		value?: string;
	}[];
}

export interface TravellerInfo {
	id: string;
	name: string;
	type: string;
	supplierTravellerType?: string;
	typeNumber: number;
}

export type TravelersById = Map<string, TravellerInfo>;

export enum WebskyEventType {
	NearestAirportsLoaded = 'nearestAirportsLoaded',
	LanguageChanged = 'languageChanged'
}

export type WebskyEvent = {
	type: WebskyEventType;
	data: any;
};

export type WithTypename<T> = T & { __typename: string };

export type Id = string;

export type SegmentId = Id;
export type PassengerId = Id;
export type ServiceId = Id;

export interface AuthDuplicates {
	login: string;
	code: string;
	isExistDuplicate: boolean;
}

export type PassengerWithServicesAndID = Pick<PassengerFragment, 'services' | 'id'>;

export type GdsService = GdsServicesFragment['services'][0];

export type GdsBaggageAndVipServiceType = GdsServiceVipSubType | BaggageType;

export interface GdsServicesLimitInfoByParams<T = number> {
	rfisc: {
		[rfisc: string]: T;
	};
	type: {
		[type in OrderAdditionalServiceGdsServiceServiceType]?: T;
	};
	subType: {
		[subType in GdsBaggageAndVipServiceType]?: T;
	};
}

export interface PassengerGdsServicesLimitInfo<T = number> {
	segments: { [segmentId: string]: GdsServicesLimitInfoByParams<T> };
	total: GdsServicesLimitInfoByParams<T>;
}

export interface GdsServicesLimitInfo<T = number> {
	[passengerId: string]: PassengerGdsServicesLimitInfo<T>;
}

export type KeyByRestriction = 'rfisc' | 'type' | 'subType';

export type RestrictionPerType = 'order' | 'segment';

export interface GdsServiceLimitInfo {
	maxCount: number;
	selectedCount: number;
	diffCount: number;
	restrictionValue: string[];
	restrictionType: GdsServiceRestrictionType;
	restrictionPerType: RestrictionPerType;
	key: KeyByRestriction;
}

export interface GdsServicesCalculatedLimitInfo {
	[passengerId: string]: {
		[segmentId: string]: GdsServicesLimitInfoByParams<GdsServiceLimitInfo>;
	};
}

export type PassengerName = 'firstName' | 'lastName' | 'middleName';
export type PassengerValueName = Partial<Record<PassengerName, string>>;

export type ClassesMap = Record<string, string | undefined>;
